import { useState } from "react";
import { Check, X, CheckCheck, CalendarDays, Clock, Phone, Mail, MapPin } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { formatDate, formatTime, formatPrice } from "@/lib/format";
import { StatusBadge, EmptyState } from "@/components/common";

const FILTERS = [["today", "Today"], ["upcoming", "Upcoming"], ["pending", "Pending"], ["past", "Past"], ["cancelled", "Cancelled"], ["all", "All"]];

function applyFilter(items, f, today) {
  const active = (b) => ["pending", "confirmed"].includes(b.status);
  switch (f) {
    case "today": return items.filter((b) => b.date === today && b.status !== "cancelled");
    case "upcoming": return items.filter((b) => active(b) && !b.is_past);
    case "pending": return items.filter((b) => b.status === "pending");
    case "past": return items.filter((b) => b.status === "completed" || (b.is_past && b.status !== "cancelled")).reverse();
    case "cancelled": return items.filter((b) => b.status === "cancelled").reverse();
    default: return [...items].reverse();
  }
}

function Actions({ b, reload }) {
  const act = async (url, body, msg) => {
    try { await api.post(url, body); toast.success(msg); reload(); } catch (e) { toast.error(formatApiError(e)); }
  };
  const btn = "inline-flex h-10 items-center gap-1 rounded-xl px-3 text-sm font-semibold transition-colors";
  const rr = b.reschedule_request;
  return (
    <div className="flex flex-wrap gap-2">
      {b.status === "pending" && <button data-testid={`pb-confirm-${b.id}`} onClick={() => act(`/provider/bookings/${b.id}/status`, { status: "confirmed" }, "Booking confirmed")} className={`${btn} bg-sky-600 text-white hover:bg-sky-700`}><Check className="h-4 w-4" />Confirm</button>}
      {["pending", "confirmed"].includes(b.status) && <button data-testid={`pb-complete-${b.id}`} onClick={() => act(`/provider/bookings/${b.id}/status`, { status: "completed" }, "Marked as completed")} className={`${btn} bg-teal-600 text-white hover:bg-teal-700`}><CheckCheck className="h-4 w-4" />Complete</button>}
      {["pending", "confirmed"].includes(b.status) && <button data-testid={`pb-cancel-${b.id}`} onClick={() => window.confirm("Cancel this booking?") && act(`/provider/bookings/${b.id}/status`, { status: "cancelled" }, "Booking cancelled")} className={`${btn} border border-rose-200 text-rose-600 hover:bg-rose-50`}><X className="h-4 w-4" />Cancel</button>}
      {rr?.status === "pending" && ["pending", "confirmed"].includes(b.status) && (
        <>
          <button data-testid={`pb-resched-accept-${b.id}`} onClick={() => act(`/provider/bookings/${b.id}/reschedule`, { action: "accept" }, "Reschedule accepted")} className={`${btn} border border-sky-300 text-sky-700`}>Accept new time</button>
          <button data-testid={`pb-resched-decline-${b.id}`} onClick={() => act(`/provider/bookings/${b.id}/reschedule`, { action: "decline" }, "Reschedule declined")} className={`${btn} border border-slate-300 text-slate-600`}>Decline</button>
        </>
      )}
    </div>
  );
}

function Row({ b, reload }) {
  const rr = b.reschedule_request;
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={`provider-booking-${b.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2"><StatusBadge status={b.status} testId={`pb-status-${b.id}`} /><span className="font-mono text-xs text-slate-400">{b.reference}</span></div>
          <p className="mt-2 font-heading text-lg font-semibold text-slate-900">{b.service_name} · {formatPrice(b.price)}</p>
          <div className="mt-1 flex flex-wrap gap-4 text-sm text-slate-600">
            <span className="flex items-center gap-1"><CalendarDays className="h-4 w-4" />{formatDate(b.date)}</span>
            <span className="flex items-center gap-1"><Clock className="h-4 w-4" />{formatTime(b.start_time)} - {formatTime(b.end_time)}</span>
          </div>
        </div>
        <Actions b={b} reload={reload} />
      </div>
      <div className="mt-4 flex flex-wrap gap-x-5 gap-y-1 border-t border-slate-100 pt-3 text-sm text-slate-600">
        <span className="font-semibold text-slate-800">{b.customer_name}</span>
        <span className="flex items-center gap-1"><Phone className="h-3.5 w-3.5" />{b.customer_phone}</span>
        <span className="flex items-center gap-1"><Mail className="h-3.5 w-3.5" />{b.customer_email}</span>
        {b.address && <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{b.address}</span>}
      </div>
      {b.notes && <p className="mt-2 text-sm italic text-slate-500">"{b.notes}"</p>}
      {rr?.status === "pending" && <p className="mt-3 rounded-lg bg-sky-50 px-3 py-2 text-sm text-sky-800">Customer requested: {formatDate(rr.date)} at {formatTime(rr.start_time)} {rr.note && `- "${rr.note}"`}</p>}
    </div>
  );
}

export default function ProviderBookings({ bookings, reload, today }) {
  const [filter, setFilter] = useState("upcoming");
  const list = applyFilter(bookings, filter, today);
  return (
    <div>
      <div className="mb-5 flex gap-2 overflow-x-auto no-scrollbar">
        {FILTERS.map(([k, l]) => (
          <button key={k} data-testid={`pb-filter-${k}`} onClick={() => setFilter(k)} className={`h-10 whitespace-nowrap rounded-full px-4 text-sm font-semibold transition-colors ${filter === k ? "bg-slate-900 text-white" : "bg-white text-slate-600 border border-slate-200"}`}>
            {l} ({applyFilter(bookings, k, today).length})
          </button>
        ))}
      </div>
      <div className="space-y-3">
        {list.length === 0 ? <EmptyState icon={CalendarDays} title="No bookings here" testId="pb-empty" /> : list.map((b) => <Row key={b.id} b={b} reload={reload} />)}
      </div>
    </div>
  );
}
